import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigateRef = useRef(null);

  // Vérifier la validité du token au montage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const tokenTimestamp = localStorage.getItem('token_timestamp');
    
    // Invalider le token après 24h
    if (storedToken && tokenTimestamp) {
      const hoursSinceLogin = (Date.now() - parseInt(tokenTimestamp)) / (1000 * 60 * 60);
      if (hoursSinceLogin > 24) {
        console.log('Token expiré, déconnexion automatique');
        logout();
        return;
      }
    }
    
    if (storedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      const userData = response.data;
      
      // Vérifier la cohérence avec le localStorage (skip si impersonation)
      const storedUserRole = localStorage.getItem('user_role');
      const isImpersonating = localStorage.getItem('impersonating') === 'true';
      if (storedUserRole && storedUserRole !== userData.role && !isImpersonating) {
        console.warn('Rôle utilisateur incohérent, déconnexion propre');
        logout();
        return;
      }
      
      setUser(userData);
      localStorage.setItem('user_role', userData.role);
      localStorage.setItem('user_id', userData.id);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Token invalide ou expiré, déconnecter proprement
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Nettoyer d'abord toutes les anciennes données
      localStorage.clear();
      
      // Stocker le token avec un timestamp
      localStorage.setItem('token', access_token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      localStorage.setItem('user_role', userData.role);
      localStorage.setItem('user_id', userData.id);
      localStorage.setItem('session_id', `${Date.now()}_${Math.random()}`);
      
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      // Nettoyer d'abord toutes les anciennes données
      localStorage.clear();
      
      // Stocker le token avec un timestamp
      localStorage.setItem('token', access_token);
      localStorage.setItem('token_timestamp', Date.now().toString());
      localStorage.setItem('user_role', newUser.role);
      localStorage.setItem('user_id', newUser.id);
      localStorage.setItem('session_id', `${Date.now()}_${Math.random()}`);
      
      setToken(access_token);
      setUser(newUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return newUser;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    // Nettoyer complètement le localStorage
    localStorage.clear();

    // Nettoyer le sessionStorage aussi
    sessionStorage.clear();

    // Synchroniser les mises à jour d'état avant la navigation
    flushSync(() => {
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    });

    // Navigation après que les états soient mis à jour
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export { AuthContext };
